import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import DeleteCompanyJob from "./partials/DeleteCompanyTopic/DeleteCompanyTopic";
import { ICompanyJobInputEdit } from "models/companyJob";
import { useTranslation } from "react-i18next";
import { getPermission, getUserData } from "helpers";
import BlockIcon from "@mui/icons-material/Block";
import useFetchSection from "hooks/useFetchSection";
import { ICompanyTopic, ICompanyTopicInput } from "models/companyTopics";
import {
  addCompanyTopics,
  CompanyTopicsCells,
  deleteCompanyTopic,
  editCompanyTopic,
  getCompanyTopics,
} from "framework/companyTopics";
import AddCompanyTopic from "./partials/AddCompanyTopic/AddCompanyTopic";
import { ITopic } from "models/topic";
import { getTopics } from "framework/topics";
import EditCompanyTopic from "./partials/EditCompanyTopic/EditCompanyTopic";
interface Props {
  feature: Feature;
}

const CompanyTopics: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);

  const [FilterCompanyTopics, setFilterCompanyTopics] = useState<
    ICompanyTopic[]
  >([] as ICompanyTopic[]);
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompanyTopic, setSelectedCompanyTopic] =
    useState<ICompanyTopic>({} as ICompanyTopic);

  const [inputObj, setInputObj] = useState<ICompanyJobInputEdit>(
    {} as ICompanyJobInputEdit
  );

  const [Topics, setTopics] = useState<ITopic[]>([] as ITopic[]);
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addTopicsMutation = useMutation({
    mutationFn: (createInput: ICompanyTopicInput[]) => {
      return addCompanyTopics(createInput);
    },
  });

  const editTopicMutation = useMutation({
    mutationFn: (createInput: ICompanyTopicInput) => {
      return editCompanyTopic(createInput, selectedCompanyTopic.id);
    },
  });

  const deleteTopicMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCompanyTopic(id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading2(true);
    try {
      const res = await queryClient.fetchQuery("get Topics", () => getTopics());
      setTopics(res);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const {
    loading,
    data: CompanyTopic,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompanyTopics(),
    fetchPermission: () => getPermission(35),
    section: "Company Topics",
    onSuccess: (data) => setFilterCompanyTopics(data),
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleOpenAdd = () => setOpenAdd(true);

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setSelectedCompanyTopic({} as ICompanyTopic);
  };

  const handleOpenEdit = (Country: ICompanyTopic) => {
    setSelectedCompanyTopic(Country);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompanyTopic({} as ICompanyTopic);
  };

  const handleOpenDelete = (Country: ICompanyTopic) => {
    setSelectedCompanyTopic(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompanyTopic({} as ICompanyTopic);
  };

  const getBodyCells = (row: ICompanyTopic) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async (data: ICompanyTopicInput[]) => {
    if (!!!data.length)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addTopicsMutation.mutateAsync(data);
      setAlert({
        open: true,
        message: "Company Topic added successfully",
        type: "success",
      });
      fetchData();
      revalidate();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async (data: ICompanyTopicInput) => {
    try {
      setLoading2(true);

      await editTopicMutation.mutateAsync({
        id: selectedCompanyTopic.id,
        name: data.name || selectedCompanyTopic.name,
          nameAr: Object.prototype.hasOwnProperty.call(data, 'nameAr') ? data.nameAr : selectedCompanyTopic.nameAr,
      });
      setAlert({
        open: true,
        message: "Company Topic Edited successfully",
        type: "success",
      });
      fetchData();
      revalidate();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteTopicMutation.mutateAsync(selectedCompanyTopic.id);
      setAlert({
        open: true,
        message: "Company Topic deleted successfully",
        type: "success",
      });
      fetchData();
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanyTopics(CompanyTopic!);
    let filtered = CompanyTopic!.filter((companyJob) =>
      (companyJob.name + ' ' + companyJob.nameAr).toLowerCase().includes(value.toLowerCase())
    );
    setFilterCompanyTopics(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />

            {permission?.hasModifyPermission && (
              <Tooltip title="Add CompanyJobs">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterCompanyTopics.length > 0 && (
          <List
            headCells={CompanyTopicsCells}
            data={FilterCompanyTopics}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCompanyTopics || FilterCompanyTopics.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_topic_header")}
              message={t("T_no_topic_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCompanyTopic
          companyTopics={CompanyTopic!}
          open={openAdd}
          loading={loading2}
          topics={Topics}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
        />
      )}

      {openEdit && (
        <EditCompanyTopic
          topics={Topics}
          open={openEdit}
          loading={loading2}
          selectedTopic={selectedCompanyTopic}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
        />
      )}
      {openDelete && (
        <DeleteCompanyJob
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CompanyTopics;
